import { useMemo, type ChangeEvent, type RefObject } from 'react';
import {
  getSiteResourceDisplayLink,
  getSiteResourceDisplayNote,
  getSiteResourceDisplayTitle,
} from '@/lib/siteResource';
import type { SiteResource } from '@/types/site';
import {
  ASSET_EDITOR_KIND_META,
  ASSET_EDITOR_KIND_ORDER,
  getAssetTransportKind,
  getAssetEditorKind,
  getAssetEditorMeta,
  getAssetEditorSubtypeOptions,
  getAssetEditorSubtypeValue,
  getDraftPrimaryText,
  getLocatorConfig,
  getResourceFilterKind,
  getResourceSubtypeFieldLabel,
  getResourceTitleFieldLabel,
  getResourceTitlePlaceholder,
  getResourceTypeFieldLabel,
  getStructuredFieldSpecs,
  getSubtypeLabel,
  type AssetEditorKind,
  type ResourceDraft,
  type ResourceFilter,
  type UpdateDraft,
} from './AutopilotResourceModal.helpers';

const FILTER_OPTIONS: ResourceFilter[] = ['all', 'image', 'pdf', 'url'];

const getFilterLabel = (value: ResourceFilter) =>
  value === 'all' ? 'All' : value === 'image' ? 'Image' : value.toUpperCase();

const getResourceListTitle = (resource: SiteResource) => getSiteResourceDisplayTitle(resource);
const getResourceListLink = (resource: SiteResource) => getSiteResourceDisplayLink(resource);
const getResourceListNote = (resource: SiteResource) => getSiteResourceDisplayNote(resource);

interface AutopilotResourceListPaneProps {
  error: string;
  filter: ResourceFilter;
  filteredResources: SiteResource[];
  loading: boolean;
  onCreateResource: () => void;
  onEditResource: (resource: SiteResource) => void;
  onFilterChange: (filter: ResourceFilter) => void;
  resources: SiteResource[];
  selectedId: number | null;
  siteDomain: string;
  siteId: number | null;
}

export const AutopilotResourceListPane = ({
  error,
  filter,
  filteredResources,
  loading,
  onCreateResource,
  onEditResource,
  onFilterChange,
  resources,
  selectedId,
  siteDomain,
  siteId,
}: AutopilotResourceListPaneProps) => {
  const summaryItems = useMemo(
    () => [
      { label: 'Total', value: resources.length },
      {
        label: 'Images',
        value: resources.filter((item) => getResourceFilterKind(item) === 'image').length,
      },
      {
        label: 'PDFs',
        value: resources.filter((item) => getResourceFilterKind(item) === 'pdf').length,
      },
      {
        label: 'URLs',
        value: resources.filter((item) => getResourceFilterKind(item) === 'url').length,
      },
    ],
    [resources],
  );

  return (
    <div className="rm-list-pane">
      <div className="rm-toolbar">
        <div className="rm-toolbar-copy">
          <div className="rm-pane-title">Current site library</div>
          <div className="rm-pane-copy">
            Autopilot can reuse these resources for {siteDomain} in page, prompt, and Google Ads
            workflows.
          </div>
        </div>
        <button className="ads-btn" type="button" disabled={!siteId} onClick={onCreateResource}>
          <i className="fa-solid fa-plus" /> Add resource
        </button>
      </div>

      <div className="rm-filter">
        {FILTER_OPTIONS.map((value) => (
          <button
            key={value}
            className={`rm-chip ${filter === value ? 'on' : ''}`}
            type="button"
            onClick={() => onFilterChange(value)}
          >
            {getFilterLabel(value)}
          </button>
        ))}
      </div>

      <div className="rm-summary">
        {summaryItems.map((item) => (
          <div key={item.label} className="rm-stat">
            <div className="rm-stat-label">{item.label}</div>
            <div className="rm-stat-value">{loading ? '--' : item.value}</div>
          </div>
        ))}
      </div>

      <div className="rm-list">
        {!siteId ? (
          <div className="rm-empty">
            <div className="rm-empty-title">No site selected</div>
            <div className="rm-empty-copy">
              Resource manager requires a valid site before it can load resources.
            </div>
          </div>
        ) : loading ? (
          <div className="rm-empty">
            <div className="rm-empty-title">Loading resources</div>
            <div className="rm-empty-copy">
              Pulling the current site resource library from the API.
            </div>
          </div>
        ) : error ? (
          <div className="rm-empty">
            <div className="rm-empty-title">Failed to load resources</div>
            <div className="rm-empty-copy">{error}</div>
          </div>
        ) : filteredResources.length ? (
          filteredResources.map((resource) => {
            const assetKind = getResourceFilterKind(resource) || 'image';
            const meta = ASSET_EDITOR_KIND_META[assetKind];

            return (
              <button
                key={resource.id}
                className={`rm-row ${selectedId === resource.id ? 'active' : ''}`}
                type="button"
                onClick={() => onEditResource(resource)}
              >
                <div className="rm-row-head">
                  <div className="rm-row-copy">
                    <div className="rm-row-meta">
                      <span className="rm-kind">
                        <i className={`fa-solid ${meta.icon}`} />
                        {meta.label}
                      </span>
                      <span className="rm-subtype">
                        {getSubtypeLabel('asset', resource.resourceSubtype)}
                      </span>
                    </div>
                    <div className="rm-row-title">{getResourceListTitle(resource)}</div>
                  </div>
                  <span className="rm-badge">
                    <i className="fa-solid fa-pen" />
                    Edit
                  </span>
                </div>
                <div className="rm-row-link">{getResourceListLink(resource)}</div>
                <div className="rm-row-note">{getResourceListNote(resource)}</div>
              </button>
            );
          })
        ) : (
          <div className="rm-empty">
            <div className="rm-empty-title">No resources found</div>
            <div className="rm-empty-copy">This site does not have any matching resources yet.</div>
          </div>
        )}
      </div>
    </div>
  );
};

interface AutopilotResourceEditorPaneProps {
  deleting: boolean;
  draft: ResourceDraft;
  onBack: () => void;
  onDraftChange: UpdateDraft;
  onRemove: () => void;
  onAssetKindChange: (kind: AssetEditorKind) => void;
  onSave: () => void;
  onSubtypeChange: (subtype: string) => void;
  onUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  saving: boolean;
  siteDomain: string;
  siteId: number | null;
  uploadInputRef: RefObject<HTMLInputElement | null>;
}

export const AutopilotResourceEditorPane = ({
  deleting,
  draft,
  onBack,
  onAssetKindChange,
  onDraftChange,
  onRemove,
  onSave,
  onSubtypeChange,
  onUpload,
  saving,
  siteDomain,
  siteId,
  uploadInputRef,
}: AutopilotResourceEditorPaneProps) => {
  const editingMode = draft.id ? 'edit' : 'create';
  const assetKind = getAssetEditorKind(draft);
  const currentSubtypeOptions = getAssetEditorSubtypeOptions(assetKind);
  const typeMeta = getAssetEditorMeta(draft);
  const subtypeLabel = getSubtypeLabel('asset', getAssetEditorSubtypeValue(draft));
  const locatorConfig = getLocatorConfig(draft);
  const uploadKind = getAssetTransportKind(draft.subtype, draft.mimeType, draft.locator);
  const canUploadReplacement = uploadKind !== 'url';
  const uploadAccept =
    uploadKind === 'pdf'
      ? 'application/pdf'
      : uploadKind === 'file'
        ? '*/*'
        : 'image/png,image/jpeg,image/svg+xml,image/webp';
  const uploadLabel =
    uploadKind === 'pdf'
      ? 'Upload replacement PDF'
      : uploadKind === 'file'
        ? 'Upload replacement file'
        : 'Upload replacement image';
  const uploadHint =
    uploadKind === 'pdf'
      ? 'Choose a PDF from your device to replace the current document asset.'
      : uploadKind === 'file'
        ? 'Choose a file from your device to replace the current asset.'
        : 'Choose a PNG, JPG, SVG, or WebP file from your device to replace the current image asset.';

  return (
    <div className="rm-editor">
      <div className="rm-editor-head">
        <button
          className="rm-back"
          type="button"
          aria-label="Back to resource list"
          onClick={onBack}
        >
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div className="rm-editor-copy">
          <div className="rm-kicker">
            {editingMode === 'create' ? 'Create resource' : 'Edit resource'}
          </div>
          <div className="rm-pane-title">
            {editingMode === 'create' ? 'Add a site resource' : getDraftPrimaryText(draft)}
          </div>
          <div className="rm-pane-copy">
            {editingMode === 'create'
              ? `Stage a new image, PDF, or URL that Autopilot can reuse for ${siteDomain}.`
              : 'Update the library entry that Autopilot can reference for this site.'}
          </div>
        </div>
      </div>

      <div className="rm-editor-body">
        <div className="rm-card">
          <div className="rm-label">Preview</div>
          <div className="rm-preview">
            <div className="rm-preview-icon">
              <i className={`fa-solid ${typeMeta.icon}`} />
            </div>
            <div className="rm-preview-copy">
              <div className="rm-preview-title">
                {getDraftPrimaryText(draft) || 'New site resource'}
              </div>
              <div className="rm-note">
                {draft.note || 'Add a reusable resource for the current site library.'}
              </div>
              <div className="rm-preview-meta">
                <span className="rm-kind">
                  <i className={`fa-solid ${typeMeta.icon}`} />
                  {typeMeta.label}
                </span>
                <span className="rm-subtype">{subtypeLabel}</span>
              </div>
            </div>
          </div>
        </div>

        {canUploadReplacement ? (
          <div className="rm-card">
            <div className="rm-label">Replace file</div>
            <div className="rm-replace">
              <div className="rm-replace-box">
                <div className="rm-replace-top">
                  <div className="rm-replace-copy">
                    <strong>{uploadLabel}</strong>
                    <span>{uploadHint}</span>
                  </div>
                  <button
                    className="ads-btn"
                    type="button"
                    disabled={!siteId || saving}
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    <i className="fa-solid fa-arrow-up-from-bracket" /> Upload
                  </button>
                </div>
                <div className="rm-upload-note">
                  Current file: {draft.locator || 'No file selected yet'}
                </div>
              </div>
              <input
                ref={uploadInputRef}
                type="file"
                accept={uploadAccept}
                hidden
                onChange={onUpload}
              />
            </div>
          </div>
        ) : null}

        <div className="rm-card">
          <div className="rm-label">Resource details</div>
          <div className="rm-field-grid" style={{ marginBottom: 10 }}>
            <div className="rm-field">
              <label htmlFor="site-resource-type">{getResourceTypeFieldLabel()}</label>
              <select
                className="rm-select"
                id="site-resource-type"
                value={assetKind}
                onChange={(event) => onAssetKindChange(event.target.value as AssetEditorKind)}
              >
                {ASSET_EDITOR_KIND_ORDER.map((value) => (
                  <option key={value} value={value}>
                    {ASSET_EDITOR_KIND_META[value].label}
                  </option>
                ))}
              </select>
            </div>

            <div className="rm-field">
              <label htmlFor="site-resource-subtype">
                {getResourceSubtypeFieldLabel(draft.resourceType)}
              </label>
              <select
                className="rm-select"
                id="site-resource-subtype"
                value={getAssetEditorSubtypeValue(draft)}
                onChange={(event) => onSubtypeChange(event.target.value)}
              >
                {currentSubtypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="rm-field" style={{ marginBottom: 10 }}>
            <label htmlFor="site-resource-title">
              {getResourceTitleFieldLabel(draft.resourceType)}
            </label>
            <input
              className="rm-input"
              id="site-resource-title"
              value={draft.title}
              placeholder={getResourceTitlePlaceholder(draft.resourceType)}
              onChange={(event) => onDraftChange({ title: event.target.value })}
            />
          </div>

          {getStructuredFieldSpecs(draft).map((field) => (
            <div
              key={field.id}
              className="rm-field"
              style={{ marginBottom: 10, gridColumn: field.wide ? '1 / -1' : undefined }}
            >
              <label htmlFor={field.id}>{field.label}</label>
              {field.kind === 'textarea' ? (
                <textarea
                  className="rm-textarea"
                  id={field.id}
                  rows={field.rows || 3}
                  placeholder={field.placeholder}
                  value={field.value}
                  onChange={(event) =>
                    onDraftChange({ [field.key]: event.target.value } as Partial<ResourceDraft>)
                  }
                />
              ) : (
                <input
                  className="rm-input"
                  id={field.id}
                  type={field.type || 'text'}
                  inputMode={field.inputMode}
                  autoComplete={field.autoComplete}
                  value={field.value}
                  placeholder={field.placeholder}
                  onChange={(event) =>
                    onDraftChange({ [field.key]: event.target.value } as Partial<ResourceDraft>)
                  }
                />
              )}
            </div>
          ))}

          {locatorConfig ? (
            <div className="rm-field" style={{ marginBottom: 10 }}>
              <label htmlFor="site-resource-locator">
                {locatorConfig.label}
                {locatorConfig.required ? '' : ' (optional)'}
              </label>
              <input
                className="rm-input"
                id="site-resource-locator"
                value={draft.locator}
                placeholder={locatorConfig.placeholder}
                onChange={(event) => onDraftChange({ locator: event.target.value })}
              />
              <div className="rm-hint">{locatorConfig.hint}</div>
            </div>
          ) : null}
        </div>

        <div className="rm-card">
          <div className="rm-label">Usage note</div>
          <div className="rm-field">
            <label htmlFor="site-resource-note">How should this resource be used?</label>
            <textarea
              className="rm-textarea"
              id="site-resource-note"
              rows={4}
              wrap="soft"
              placeholder="Describe how this resource should be used in pages, prompts, or ads."
              value={draft.note}
              onChange={(event) => onDraftChange({ note: event.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="rm-editor-foot">
        <div className="rm-foot-note">
          {editingMode === 'create'
            ? 'Creating a resource will write a new current site resource record.'
            : 'Saving will create a new current version for this resource.'}
        </div>
        <div className="rm-actions">
          {editingMode === 'edit' ? (
            <button
              className="auth-secondary rm-danger"
              type="button"
              disabled={deleting || saving}
              onClick={onRemove}
            >
              <i className="fa-solid fa-trash-can" /> Remove
            </button>
          ) : null}
          <button className="auth-secondary" type="button" disabled={saving} onClick={onBack}>
            Cancel
          </button>
          <button
            className="ads-btn"
            type="button"
            disabled={saving || deleting || !siteId}
            onClick={onSave}
          >
            <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`} />
            {editingMode === 'create' ? 'Add resource' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
